/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    DisplayHtmlContent,
    DividerNew,
    FlexCol,
    FlexRow,
    FocusOnMount,
    Heading,
    HtmlParser,
    Icon,
    ImageWithFallback,
    ModalActions,
    NotificationsWrapper,
    SecurityHelper,
    Stack,
    StyledModal,
    Text,
    useAssetImages,
    useBreakpoint,
    useFlags,
} from 'sarsaparilla';

import { HeroImageGallery } from 'shared-ui/hero-image-gallery';

import HelperWizard from './HelperWizard';
import SitePassBanner from './SitePassBanner';
import SitePassPageHeader from './SitePassPageHeader/SitePassPageHeader';
import PassSelectionPanel from './PassSelectionPanel';
import LoadingSkeleton from './loadingSkeleton/LoadingSkeleton';
import { FacilityProps, PassTypeProps } from '../props';
import RenderIf from '../utils/JSXUtils';
import { showSitePassAlert as showSitePassAlertUtil } from '../utils/launchDarklyUtils';
import { getGatewayUrl, upperCaseFirstLetter } from '../utils'; // We could use this one instead import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl'; when we add support for typescript in the widget
import PassSelectionTabs from './PassSelectionTabs';
import SitePassSelectionHeadingAndFees from './SitePassSelectionHeadingAndFees';

//Stable empty gallery reference to prevent infinite loop
const EMPTY_GALLERY = [];

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
function PassSelection({
    facility = {},
    facilityId,
    loading = {},
    loaded,
    reservationId,
    selectedPassTypeId,
    selectedPass,
    passTypes = [],
    parentAsset,
    passTypesRaw,
    errorMessage,
    status,
    passTypesOptions,
    isInWidget = false, // used for widget
    baseDomain = '', // used for widget
    onSelectPass,
    onRemovePass,
    onSaveReservation,
    parentSelector, // used for widget
}) {
    const [passSelectionState, setPassSelectionState] = useState({
        status: 'SHOW_HELPER',
        wizardStep: 0,
        isModalOpen: false,
        isNonResidentModalOpen: false,
    });
    const {
        facilitiesUsingSitePassIntegration,
        releaseDigitalIapFeatures,
        enablePassRatesModal,
    } = useFlags();
    const breakpoint = useBreakpoint();
    const { heroImage, status: heroImageStatus } = useAssetImages({
        assetId: facility?.asset_id || facility?.id,
    });

    const isNPSAgency = useMemo(() => facility?.agency === 'NPS', [facility?.agency]);
    const isWidget = SecurityHelper.isWidgetSession();

    // launch darkly flags does not work for the widget
    const shouldShowSitePassAlert = useMemo(() => {
        return (
            showSitePassAlertUtil(facilityId, facilitiesUsingSitePassIntegration) ||
            isNPSAgency ||
            isInWidget ||
            isWidget
        );
    }, [facilitiesUsingSitePassIntegration, facilityId, isInWidget, isNPSAgency]);

    const showNonResidentFees = useMemo(() => {
        return (
            loaded?.passTypes &&
            passTypes?.some((passType) => passType?.features?.non_resident_fee)
        );
    }, [passTypes, loaded]);

    const isLoading = useMemo(() => {
        return status === 'LOADING';
    }, [status]);

    const recArea = parentAsset?.find((item) => item?.entity_id === facility?.asset_id);

    const getInterAgencyPassPageUrl = () => {
        let baseUrl = '/pass';

        if (releaseDigitalIapFeatures?.passLandingPage) {
            baseUrl = `${baseUrl}#digital-interagency-passes`;
        } else {
            baseUrl = `${baseUrl}?section_id=interagency-annual-pass`;
        }

        if (baseDomain) {
            baseUrl = `${baseDomain}${baseUrl}`;
        }

        return baseUrl;
    };

    const handleGoToInterAgencyPassPage = () => {
        window.location.href = getInterAgencyPassPageUrl();
    };

    const handleReviewOptionsClick = () => {
        const el = document.getElementById('interAgencyPassesInfo');
        if (el) {
            const originalTop = Math.floor(el.getBoundingClientRect().top);
            window.scrollBy({ top: originalTop - 100, left: 0, behavior: 'smooth' });
        }
    };

    const handleViewPassesClick = () => {
        setPassSelectionState((prevState) => ({
            ...prevState,
            status: 'SHOW_ALL_PASSES',
            wizardStep: 0,
        }));
    };

    const handleViewHelperClick = () => {
        setPassSelectionState((prevState) => ({
            ...prevState,
            status: 'SHOW_HELPER',
            wizardStep: 1,
        }));
    };

    const handleBackToStart = () => {
        setPassSelectionState((prevState) => ({
            ...prevState,
            status: 'SHOW_HELPER',
            wizardStep: 0,
        }));
    };

    const handleCloseConfirmationModal = () => {
        onRemovePass();

        setPassSelectionState((prevState) => ({
            ...prevState,
            isModalOpen: false,
        }));
    };

    const handleOpenNonResidentModal = () => {
        setPassSelectionState((prevState) => ({
            ...prevState,
            isNonResidentModalOpen: true,
        }));
    };

    const handleCloseNonResidentModal = () => {
        setPassSelectionState((prevState) => ({
            ...prevState,
            isNonResidentModalOpen: false,
        }));
    };

    const handleSelectPass = (id) => {
        onSelectPass(id);

        setPassSelectionState((prevState) => ({
            ...prevState,
            isModalOpen: true,
        }));
    };

    const handleSaveReservation = () => {
        onSaveReservation();

        setPassSelectionState((prevState) => ({
            ...prevState,
            isModalOpen: false,
        }));
    };

    const renderPricing = () => {
        let pricing = null;
        if (facility.id && Array.isArray(facility?.contents?.pricing)) {
            const pricingContent = facility.contents.pricing.flatMap(
                ({ paragraphs = [] }, p) =>
                    paragraphs.map((html, n) => (
                        <p key={`html-${p}-${n}`}>
                            <HtmlParser html={html} />
                        </p>
                    ))
            );

            pricing = (
                <NotificationsWrapper noticeType="info" closeButton={false}>
                    {pricingContent}
                </NotificationsWrapper>
            );
        }

        return pricing;
    };

    const renderInterAgencyPassesInfoButton = () => {
        if (isInWidget || isWidget) {
            return (
                <Button
                    appearance="link"
                    iconBeforeElement={<Icon iconName="help-outline" />}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getInterAgencyPassPageUrl()}
                    gaTrackingId="662752139445"
                    className="pass-selection__learn-more-btn"
                >
                    Learn more about the Digital America the Beautiful Pass
                </Button>
            );
        }

        return (
            <Button
                appearance="link"
                iconBeforeElement={<Icon iconName="help-outline" />}
                onClick={handleGoToInterAgencyPassPage}
                gaTrackingId="711847152892"
                className="pass-selection__learn-more-btn"
            >
                Learn more about the Digital America the Beautiful Pass
            </Button>
        );
    };

    const renderInterAgencyPassesInfo = () => {
        const baseUrl =
            (isInWidget || isWidget) && baseDomain
                ? `${baseDomain}/shared`
                : process.env.CDN;
        return (
            <div
                id="interAgencyPassesInfo"
                className="parkpass-order-details__passes-info mb-2"
            >
                <Heading className="mb-half" headingLevel={2} appearance="h6">
                    America the Beautiful Passes cover the entrance fee at this location.
                </Heading>
                <Text className="mb-2">
                    If you have a pass, bring it with you. Frequent visitors, seniors,
                    military, and others may save money on entrance fees with an America
                    the Beautiful Pass.
                </Text>
                <div className="parkpass-order-details__passes-info-image-container mb-2">
                    <ImageWithFallback
                        imagePath={`${baseUrl}/img/interagencyPass/inter-agency-passes-info`}
                        fallbackExtension="png"
                        alt="Multiple interagency passes"
                    />
                </div>
                {renderInterAgencyPassesInfoButton()}
            </div>
        );
    };

    const renderInfoAlert = () => {
        return showNonResidentFees ? (
            <Alert type="info">
                Non-U.S. Residents must pay an additional fee for each person 16 years or
                older in the party.
                <Button
                    appearance="link"
                    className="ml-1"
                    aria-label="Learn more about Non-U.S. resident info"
                    onClick={handleOpenNonResidentModal}
                >
                    Learn More
                </Button>
            </Alert>
        ) : (
            <Alert type="info">
                You have the option to purchase a pass when you visit the park.
            </Alert>
        );
    };

    if (!loaded?.facilityByIdOrSlug || !loaded?.passTypes) {
        return <LoadingSkeleton />;
    }

    return (
        <section className="pass-selection">
            <Stack space="md">
                {RenderIf(loading.passTypes || (loaded.passTypes && !reservationId))(
                    <SitePassBanner
                        facilityId={facilityId}
                        isInWidget={false}
                        baseDomain={baseDomain}
                    />
                )}
                <FlexRow>
                    <FlexCol md={8} xsOrder="last" mdOrder="first">
                        <Stack space="md">
                            <SitePassPageHeader
                                isLoading={false}
                                heading="Site Pass Selection"
                                facilityName={facility?.name}
                                parentLinkText={recArea?.name}
                                parentLinkUrl={
                                    facility?.asset_id
                                        ? getGatewayUrl(facility?.asset_id, baseDomain)
                                        : null
                                }
                                isPartOfParent
                                favoriteFacilityId={facilityId}
                                favoriteEntityId={facility.asset_id}
                                favoriteEntityType="sitepass"
                                isInWidget={isInWidget || isWidget}
                            />

                            {RenderIf(!!errorMessage)(
                                <Alert type="error">{errorMessage}</Alert>
                            )}

                            {RenderIf(passSelectionState?.status === 'SHOW_HELPER')(
                                <HelperWizard
                                    facilityId={facilityId}
                                    facilityAgency={facility?.agency}
                                    initialStep={passSelectionState?.wizardStep}
                                    selectedPassTypeId={selectedPassTypeId}
                                    parentName={recArea?.name}
                                    passTypes={passTypes}
                                    passTypesRaw={passTypesRaw}
                                    pricing={renderPricing()}
                                    passTypesOptions={passTypesOptions}
                                    status={status}
                                    showDescription={false}
                                    isInWidget={isInWidget || isWidget}
                                    onSelectPass={handleSelectPass}
                                    onRemovePass={onRemovePass}
                                    onReviewOptionsClick={handleReviewOptionsClick}
                                    onViewAllPassesClick={handleViewPassesClick}
                                    onSaveReservation={onSaveReservation}
                                    renderInfoAlert={renderInfoAlert}
                                />
                            )}

                            {RenderIf(passSelectionState?.status === 'SHOW_ALL_PASSES')(
                                <>
                                    {renderInfoAlert()}
                                    {enablePassRatesModal ? (
                                        <SitePassSelectionHeadingAndFees
                                            facilityName={facility?.name}
                                            passTypes={passTypes}
                                        />
                                    ) : (
                                        <FlexRow
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <FlexCol>
                                                <FocusOnMount>
                                                    <Heading
                                                        appearance="h5"
                                                        headingLevel={2}
                                                    >
                                                        {`All Site Passes available at ${facility?.name}`}
                                                    </Heading>
                                                </FocusOnMount>
                                            </FlexCol>
                                        </FlexRow>
                                    )}

                                    <PassSelectionTabs
                                        selectedPassTypeId={selectedPassTypeId}
                                        passTypes={passTypes}
                                        passTypesRaw={passTypesRaw}
                                        passTypesOptions={passTypesOptions}
                                        status={status}
                                        showDescription={false}
                                        renderPricing={renderPricing}
                                        onSelectPass={handleSelectPass}
                                        onRemovePass={onRemovePass}
                                        isLoading={isLoading}
                                        handleViewHelperClick={handleViewHelperClick}
                                        handleBackToStart={handleBackToStart}
                                    />
                                </>
                            )}
                            {RenderIf(breakpoint?.isSmAndDown)(
                                <>
                                    <DividerNew />
                                    <PassSelectionPanel
                                        title={facility?.name}
                                        overview={facility?.contents?.overview}
                                    />
                                </>
                            )}

                            {RenderIf(
                                shouldShowSitePassAlert ||
                                    (shouldShowSitePassAlert && breakpoint?.isSmAndDown)
                            )(<DividerNew />)}

                            {RenderIf(shouldShowSitePassAlert)(
                                <Box>{renderInterAgencyPassesInfo()}</Box>
                            )}
                        </Stack>
                    </FlexCol>
                    <FlexCol md={4} xsOrder="first" mdOrder="last">
                        <Stack space="md">
                            {RenderIf(
                                facility?.id &&
                                    breakpoint.isSmAndUp &&
                                    !isInWidget &&
                                    !isWidget
                            )(
                                <div className="pass-selection__image-container mb-sm-3 mb-md-0">
                                    <HeroImageGallery
                                        heroImage={heroImage}
                                        galleryImages={EMPTY_GALLERY}
                                        status={heroImageStatus}
                                    />
                                </div>
                            )}
                            {RenderIf(breakpoint.isMdAndUp)(
                                <PassSelectionPanel
                                    title={facility?.name}
                                    overview={facility?.contents?.overview}
                                />
                            )}
                        </Stack>
                    </FlexCol>
                </FlexRow>
            </Stack>
            <StyledModal
                isOpen={passSelectionState?.isModalOpen}
                onRequestClose={handleCloseConfirmationModal}
                heading={`You selected the ${selectedPass?.name}`}
                parentSelector={parentSelector || undefined}
            >
                {selectedPass?.used_for && (
                    <DisplayHtmlContent
                        html={`${upperCaseFirstLetter(selectedPass?.used_for)}`}
                    />
                )}
                <ModalActions>
                    <ButtonGroup>
                        <Button
                            appearance="tertiary"
                            onClick={handleCloseConfirmationModal}
                            gaTrackingId="196887984833"
                        >
                            Keep Exploring Passes
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={handleSaveReservation}
                            gaTrackingId="047964645829"
                        >
                            Continue to Order Details
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
            <StyledModal
                isOpen={passSelectionState?.isNonResidentModalOpen}
                onRequestClose={handleCloseNonResidentModal}
                heading="Non-U.S. Resident Fee"
                parentSelector={parentSelector || undefined}
            >
                <p>
                    A U.S. Resident is defined as a United States citizen or legal
                    resident of the United States.
                </p>
                <p>
                    If you are not a Resident of the United States or there are non-U.S.
                    Residents in your party, please take into account that this facility
                    charges an additional fee for each 16 and older visitor, it must be
                    paid upon purchase or arrival at the entrance of the facility.
                </p>
                <p>
                    <span style={{ fontWeight: 'bold' }}>Note:</span> U.S. citizens or
                    residents 16 or older may be required to show proof of U.S.
                    citizenship or residency upon arrival. Example documents include
                    passport, driver&apos;s license, permanent resident card.
                </p>
                <ModalActions>
                    <Button appearance="primary" onClick={handleCloseNonResidentModal}>
                        Okay
                    </Button>
                </ModalActions>
            </StyledModal>
        </section>
    );
}

PassSelection.propTypes = {
    facility: FacilityProps,
    facilityId: PropTypes.string,
    loaded: PropTypes.shape({
        facilityByIdOrSlug: PropTypes.bool,
        reservations: PropTypes.bool,
        passTypes: PropTypes.bool,
    }),
    loading: PropTypes.shape({
        facilities: PropTypes.bool,
        passTypes: PropTypes.bool,
    }),
    errorMessage: PropTypes.string,
    status: PropTypes.string,
    passTypesOptions: PropTypes.array,
    isInWidget: PropTypes.bool,
    baseDomain: PropTypes.string,
    passTypes: PropTypes.arrayOf(PassTypeProps),
    selectedPassTypeId: PropTypes.string,
    selectedPass: PropTypes.object,
    passTypesRaw: PropTypes.arrayOf(PassTypeProps),
    reservationId: PropTypes.string,
    parentAsset: PropTypes.array,
    onSelectPass: PropTypes.func.isRequired,
    onRemovePass: PropTypes.func.isRequired,
    onSaveReservation: PropTypes.func.isRequired,
    /** Used by ui-widget for the Modal to change the parent element that the modal will be attached to */
    parentSelector: PropTypes.func,
};

export default PassSelection;
